/**
 * DEV-15384 — Kit (bundle) PDP: step builder, gallery and accordions.
 *
 * <kit-builder> drives the steps rendered by sections/kit-main.liquid and
 * snippets/kit-step.liquid. Each step is a form built on the same data as
 * the component's live PDP; nothing here changes how a field submits:
 * native selects/inputs stay the source of truth and keep the data contract
 * of assets/z__jsCustomOptions.js.
 *
 * Add to cart sends ONE kit line (fixed price) carrying every step as
 * _kit_N = {"v": variantId, "p": {property: value}, "a": hasAddons} plus a
 * readable _kit_N_display for the cart. The tactipup-functions cart
 * transform expands it into the component products with those properties.
 * Paid add-ons are their own lines, linked through _addon_key/_addon_for.
 */
(() => {
  if (customElements.get('kit-builder')) return;

  const DESKTOP_QUERY = '(min-width: 799px)';
  const COLOR_ALIASES = { gray: 'wolf gray' };
  const SOLID_SUFFIX = /^\(solid\)$/i;
  const CTA_VISIBLE_RATIO = 0.99;

  const normalizeColor = (value) => {
    const base = String(value)
      .toLowerCase()
      .replace(/\(solid\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return COLOR_ALIASES[base] ?? base;
  };

  const formatMoney = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    return `$${(cents / 100).toFixed(2)}`;
  };

  const scrollOffset = () => {
    const announcement = document.querySelector('.announcement-sticky-wrapper');
    return (announcement?.offsetHeight ?? 0) + 16;
  };

  const scrollToElement = (element) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = element.getBoundingClientRect().top + window.scrollY - scrollOffset();
    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  // True when the element is fully visible between the sticky announcement
  // bar and the kit's sticky bar, so a validation tap doesn't move the page.
  const isInView = (element) => {
    const rect = element.getBoundingClientRect();
    const sticky = document.querySelector('.kit-sticky--visible');
    const stickyHeight = sticky && getComputedStyle(sticky).display !== 'none' ? sticky.offsetHeight : 0;
    return rect.top >= scrollOffset() && rect.bottom <= window.innerHeight - stickyHeight;
  };

  const fieldControl = (field) => field.querySelector('input, select, textarea');

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[character]);

  /**
   * Listbox UI over a native <select>. The select keeps the value (and its
   * name, if any) so forms, z__jsCustomOptions.js and variant resolution read
   * it unchanged; every pick dispatches a bubbling `change` on it.
   */
  class KitSelect {
    constructor(select) {
      this.select = select;
      this.field = select.closest('[data-kit-field]');
      this.label = this.field.querySelector('.kit-field__label');
      this.activeIndex = -1;
      this.typeahead = '';
      this.typeaheadTimer = null;

      this.onButtonClick = this.onButtonClick.bind(this);
      this.onButtonKeydown = this.onButtonKeydown.bind(this);
      this.onListKeydown = this.onListKeydown.bind(this);
      this.onListClick = this.onListClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onSelectChange = this.onSelectChange.bind(this);

      this.build();
      this.sync();
    }

    build() {
      const listId = `${this.select.id}-listbox`;
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'kit-select__button';
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', listId);
      this.button.innerHTML = `
        <span class="kit-select__value"></span>
        <svg class="kit-select__caret" width="14" height="8" viewBox="0 0 13.398 7.588" fill="none" aria-hidden="true" focusable="false"><path d="M6.699 7.588L0 0.7854L0.798 0L6.699 5.992L12.6 0L13.398 0.7854L6.699 7.588Z" fill="currentColor"/></svg>`;
      this.valueSlot = this.button.querySelector('.kit-select__value');

      this.list = document.createElement('ul');
      this.list.className = 'kit-select__list';
      this.list.id = listId;
      this.list.setAttribute('role', 'listbox');
      this.list.setAttribute('tabindex', '-1');
      if (this.label?.id) {
        this.list.setAttribute('aria-labelledby', this.label.id);
        this.button.setAttribute('aria-labelledby', `${this.label.id} ${this.select.id}-value`);
      }
      this.valueSlot.id = `${this.select.id}-value`;
      this.list.hidden = true;

      const wrapper = document.createElement('div');
      wrapper.className = 'kit-select';
      this.select.after(wrapper);
      wrapper.append(this.button, this.list);
      this.select.classList.add('kit-field__native--enhanced');
      this.select.tabIndex = -1;
      this.select.setAttribute('aria-hidden', 'true');
      this.label?.removeAttribute('for');

      this.button.addEventListener('click', this.onButtonClick);
      this.button.addEventListener('keydown', this.onButtonKeydown);
      this.list.addEventListener('keydown', this.onListKeydown);
      this.list.addEventListener('click', this.onListClick);
      this.select.addEventListener('change', this.onSelectChange);
    }

    options() {
      return [...this.select.options].filter((option) => option.value !== '' && !option.hasAttribute('data-kit-none'));
    }

    static optionMarkup(option) {
      const text = option.textContent.trim();
      // Labelled values (field block "value labels") carry their own detail, e.g. Small + 14” - 17”
      const labelled = 'kitDetail' in option.dataset;
      const split = labelled ? -1 : text.indexOf(' (');
      const name = escapeHtml(split > 0 ? text.slice(0, split) : text);
      const detailText = labelled ? option.dataset.kitDetail : split > 0 ? text.slice(split + 1) : '';
      const detail = SOLID_SUFFIX.test(detailText) ? '' : escapeHtml(detailText);
      const swatch = option.dataset.swatch
        ? `<img class="kit-select__swatch${option.hasAttribute('data-swatch-wide') ? ' kit-select__swatch--wide' : ''}" src="${escapeHtml(option.dataset.swatch)}" alt="" width="36" height="36" loading="lazy">`
        : '';
      const price = option.dataset.kitPriceLabel
        ? `<span class="kit-select__price">${escapeHtml(option.dataset.kitPriceLabel)}</span>`
        : '';
      const detailMarkup = detail ? `<span class="kit-select__detail">${detail}</span>` : '';
      return `${swatch}<span class="kit-select__text${labelled ? ' kit-select__text--labelled' : ''}"><span class="kit-select__name">${name}</span>${detailMarkup}${price}</span>`;
    }

    renderList() {
      const options = this.options();
      this.list.innerHTML = options
        .map((option, index) => {
          const selected = option.value === this.select.value;
          return `<li class="kit-select__option" role="option" id="${this.list.id}-${index}" data-value="${escapeHtml(option.value)}" aria-selected="${selected}"${option.disabled ? ' aria-disabled="true"' : ''}>${KitSelect.optionMarkup(option)}</li>`;
        })
        .join('');
    }

    sync() {
      const option = this.select.selectedOptions[0];
      const filled = Boolean(option && option.value !== '' && !option.hasAttribute('data-kit-none'));
      this.field.classList.toggle('is-filled', filled);
      this.valueSlot.innerHTML = filled
        ? KitSelect.optionMarkup(option)
        : `<span class="kit-select__placeholder" aria-hidden="true">${escapeHtml(this.select.options[0]?.textContent.trim() ?? '')}</span>`;
      if (!this.list.hidden) this.renderList();
    }

    isOpen() {
      return !this.list.hidden;
    }

    open() {
      if (this.isOpen()) return;
      this.renderList();
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.field.classList.add('is-open');
      const items = this.items();
      const selectedIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      this.setActive(selectedIndex >= 0 ? selectedIndex : this.nextEnabled(-1, 1));
      this.list.focus({ preventScroll: true });
      document.addEventListener('click', this.onDocumentClick, true);
    }

    close(focusButton = false) {
      if (!this.isOpen()) return;
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.list.removeAttribute('aria-activedescendant');
      this.field.classList.remove('is-open');
      document.removeEventListener('click', this.onDocumentClick, true);
      if (focusButton) this.button.focus({ preventScroll: true });
    }

    items() {
      return [...this.list.querySelectorAll('[role="option"]')];
    }

    nextEnabled(from, step) {
      const items = this.items();
      for (let index = from + step; index >= 0 && index < items.length; index += step) {
        if (items[index].getAttribute('aria-disabled') !== 'true') return index;
      }
      return from;
    }

    setActive(index) {
      const items = this.items();
      items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
      this.activeIndex = index;
      if (items[index]) {
        this.list.setAttribute('aria-activedescendant', items[index].id);
        items[index].scrollIntoView({ block: 'nearest' });
      }
    }

    choose(index) {
      const item = this.items()[index];
      if (!item || item.getAttribute('aria-disabled') === 'true') return;
      this.select.value = item.dataset.value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close(true);
    }

    onSelectChange() {
      this.sync();
    }

    onButtonClick() {
      if (this.isOpen()) {
        this.close(true);
      } else {
        this.open();
      }
    }

    onButtonKeydown(event) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.open();
      }
    }

    onListKeydown(event) {
      const items = this.items();
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(this.nextEnabled(this.activeIndex, 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(this.nextEnabled(this.activeIndex, -1));
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(this.nextEnabled(-1, 1));
          break;
        case 'End':
          event.preventDefault();
          this.setActive(this.nextEnabled(items.length, -1));
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.choose(this.activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'Tab':
          this.close();
          break;
        default:
          this.onTypeahead(event, items);
      }
    }

    onTypeahead(event, items) {
      if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
      window.clearTimeout(this.typeaheadTimer);
      this.typeahead += event.key.toLowerCase();
      this.typeaheadTimer = window.setTimeout(() => {
        this.typeahead = '';
      }, 600);
      const match = items.findIndex(
        (item) => item.getAttribute('aria-disabled') !== 'true'
          && item.textContent.trim().toLowerCase().startsWith(this.typeahead)
      );
      if (match >= 0) this.setActive(match);
    }

    onListClick(event) {
      const item = event.target.closest('[role="option"]');
      if (!item) return;
      this.choose(this.items().indexOf(item));
    }

    onDocumentClick(event) {
      if (this.button.contains(event.target) || this.list.contains(event.target)) return;
      this.close();
    }
  }

  /**
   * One builder step: variant resolution, toggles, completeness and the
   * properties it contributes to the kit line.
   */
  class KitStep {
    constructor(panel, index) {
      this.panel = panel;
      this.index = index;
      this.form = panel.querySelector('[data-kit-step-form]');
      this.product = JSON.parse(panel.querySelector('[data-kit-product]').textContent);
      this.variantInput = this.form.querySelector('[data-kit-variant-id]');
      // Fields follow the Figma order; availability works in product option order.
      this.variantSelects = [...this.form.querySelectorAll('[data-kit-option-index]')]
        .sort((a, b) => Number(a.dataset.kitOptionIndex) - Number(b.dataset.kitOptionIndex));
      this.cta = panel.querySelector('[data-kit-cta]');
      this.error = panel.querySelector('[data-kit-error]');
      this.colorField = this.form.querySelector('[data-kit-color-field]');
      this.selects = new Map();
      this.showingErrors = false;
      this.variant = null;

      this.form.querySelectorAll('[data-kit-select]').forEach((select) => {
        this.selects.set(select, new KitSelect(select));
      });
      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.bindToggles();
      this.syncRestoredState();
      this.applyDefaults();
      this.updateAvailability();
      this.resolveVariant();
    }

    // Field block "preselected value": first listed value in stock wins. Values the
    // browser restored and disabled fields (e.g. behind "Add Personalization?") are left alone.
    applyDefaults() {
      this.form.querySelectorAll('[data-kit-select][data-kit-default]').forEach((select) => {
        if (select.value !== '' || select.disabled) return;
        const isVariantSelect = this.variantSelects.includes(select);
        if (isVariantSelect) this.updateAvailability();
        const wanted = select.dataset.kitDefault.split('\n').map((value) => value.trim()).filter(Boolean);
        const option = wanted
          .map((value) => [...select.options].find((candidate) => candidate.value === value && !candidate.disabled))
          .find(Boolean);
        if (!option) return;
        select.value = option.value;
        if (isVariantSelect) {
          this.updateAvailability();
        } else {
          this.selects.get(select)?.sync();
        }
      });
    }

    // Browsers restore form values on back/forward navigation: bring the
    // toggles, revealed fields and floating labels in line with them.
    syncRestoredState() {
      this.form.querySelectorAll('[data-kit-addon-toggle-input]').forEach((toggle) => {
        const wrapper = document.getElementById(toggle.getAttribute('aria-controls'));
        const select = wrapper?.querySelector('select');
        const chosen = select?.selectedOptions[0];
        toggle.checked = Boolean(chosen && chosen.value !== '' && !chosen.hasAttribute('data-kit-none'));
        wrapper.hidden = !toggle.checked;
        toggle.setAttribute('aria-expanded', String(toggle.checked));
      });
      this.form.querySelectorAll('[data-kit-reveal-controller] input[type="checkbox"]').forEach((checkbox) => {
        this.onRevealToggle(checkbox);
      });
      this.form.querySelectorAll('.kit-field--text input').forEach((input) => {
        input.closest('[data-kit-field]').classList.toggle('is-filled', input.value !== '');
      });
    }

    bindToggles() {
      this.form.querySelectorAll('[data-kit-addon-toggle-input]').forEach((toggle) => {
        toggle.addEventListener('change', () => this.onAddonToggle(toggle));
      });
      this.form.querySelectorAll('[data-kit-reveal-controller] input[type="checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => this.onRevealToggle(checkbox));
      });
    }

    onAddonToggle(toggle) {
      const wrapper = document.getElementById(toggle.getAttribute('aria-controls'));
      const select = wrapper?.querySelector('select');
      if (!select) return;
      const none = select.querySelector('[data-kit-none]');
      wrapper.hidden = !toggle.checked;
      toggle.setAttribute('aria-expanded', String(toggle.checked));
      select.value = toggle.checked ? '' : none?.value ?? '';
      wrapper.classList.remove('custom-options__field--error');
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onRevealToggle(checkbox) {
      this.form.querySelectorAll(`[data-kit-reveal-for="${checkbox.id}"]`).forEach((field) => {
        field.hidden = !checkbox.checked;
        field.classList.remove('custom-options__field--error');
        const control = fieldControl(field);
        if (control) control.disabled = !checkbox.checked;
      });
    }

    chosenOptions() {
      return this.variantSelects.map((select) => select.value || null);
    }

    updateAvailability() {
      const chosen = this.chosenOptions();
      this.variantSelects.forEach((select, optionIndex) => {
        [...select.options].forEach((option) => {
          if (option.value === '') return;
          option.disabled = !this.product.variants.some((variant) => variant.available
            && variant.options[optionIndex] === option.value
            && chosen.every((value, index) => index === optionIndex || value === null || variant.options[index] === value));
        });
        this.selects.get(select)?.sync();
      });
    }

    resolveVariant() {
      const chosen = this.chosenOptions();
      if (!this.variantSelects.length) {
        this.variant = this.product.variants.find((variant) => variant.available) ?? null;
      } else {
        const complete = chosen.every((value) => value !== null);
        this.variant = complete
          ? this.product.variants.find((variant) => variant.available
            && variant.options.every((value, index) => value === chosen[index])) ?? null
          : null;
      }

      this.variantInput.value = this.variant ? String(this.variant.id) : '';
      this.form.querySelectorAll('[data-kit-variant-property]').forEach((input) => {
        input.value = this.variant ? this.variant.options[Number(input.dataset.optionIndex)] : '';
        input.disabled = !this.variant;
      });
    }

    onChange(target) {
      if (this.variantSelects.includes(target)) {
        this.updateAvailability();
        this.resolveVariant();
      }
      if (target.closest('[data-kit-field]')) {
        target.closest('[data-kit-field]').classList.remove('custom-options__field--error');
      }
    }

    missingFields() {
      const missing = [];
      this.form.querySelectorAll('[data-kit-field][data-kit-required]').forEach((field) => {
        if (field.hidden) return;
        const control = fieldControl(field);
        if (!control || control.disabled) return;
        const filled = control.type === 'checkbox' ? control.checked : control.value.trim() !== '';
        if (!filled) missing.push(field);
      });

      const allChosen = this.chosenOptions().every((value) => value !== null);
      if (allChosen && !this.variant && this.variantSelects.length) {
        const lastField = [...this.form.querySelectorAll('[data-kit-option-index]')].pop().closest('[data-kit-field]');
        if (!missing.includes(lastField)) missing.push(lastField);
      }

      return missing.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    }

    isComplete() {
      return this.missingFields().length === 0;
    }

    showErrors() {
      const missing = this.missingFields();
      this.form.querySelectorAll('[data-kit-field][data-kit-required]').forEach((field) => {
        field.classList.toggle('custom-options__field--error', missing.includes(field));
      });
      this.showingErrors = missing.length > 0;
      this.error.textContent = missing[0]?.dataset.kitMessage ?? '';
      return missing;
    }

    refreshErrors() {
      if (!this.showingErrors) return;
      const missing = this.missingFields();
      this.error.textContent = missing[0]?.dataset.kitMessage ?? '';
      if (!missing.length) this.showingErrors = false;
    }

    clearErrors() {
      this.showingErrors = false;
      this.error.textContent = '';
      this.form.querySelectorAll('.custom-options__field--error').forEach((field) => {
        field.classList.remove('custom-options__field--error');
      });
    }

    focusField(field) {
      if (!isInView(field)) scrollToElement(field);
      const enhanced = field.querySelector('.kit-select__button');
      const control = enhanced ?? fieldControl(field);
      control?.focus({ preventScroll: true });
    }

    addons() {
      const addons = [];
      this.form.querySelectorAll('[data-addon-select]').forEach((select) => {
        if (select.disabled) return;
        const option = select.selectedOptions[0];
        if (option?.dataset.addonVariantId) {
          addons.push({ id: Number(option.dataset.addonVariantId), price: Number(option.dataset.addonPrice) || 0 });
        }
      });
      this.form.querySelectorAll('[data-addon-checkbox]:checked').forEach((checkbox) => {
        if (checkbox.disabled) return;
        addons.push({ id: Number(checkbox.dataset.addonVariantId), price: Number(checkbox.dataset.addonPrice) || 0 });
      });
      return addons;
    }

    properties() {
      let formData = new FormData(this.form);
      if (window.Shopify?.theme?.customOptions) {
        formData = window.Shopify.theme.customOptions.cleanFormData(formData, this.form);
      }
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[([\s\S]*)\]$/);
        if (match) properties[match[1]] = value;
      }

      // Same order as the live PDP: variant options in product order, then the rest.
      const ordered = {};
      [...this.form.querySelectorAll('[data-kit-variant-property]')]
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .forEach((input) => {
          const key = input.name.match(/^properties\[([\s\S]*)\]$/)?.[1];
          if (key !== undefined && key in properties) ordered[key] = properties[key];
        });
      Object.entries(properties).forEach(([key, value]) => {
        if (!(key in ordered)) ordered[key] = value;
      });
      return ordered;
    }

    display(properties) {
      const lines = [this.product.title];
      const entries = Object.entries(properties);
      if (entries.length) {
        entries.forEach(([key, value]) => lines.push(`${key.trim()}: ${value}`));
      } else if (this.variant) {
        this.product.options.forEach((name, index) => lines.push(`${name}: ${this.variant.options[index]}`));
      }
      return lines.join('\n');
    }

    payload() {
      const properties = this.properties();
      return {
        variantId: this.variant.id,
        properties,
        display: this.display(properties),
        addons: this.addons(),
      };
    }
  }

  class KitBuilder extends HTMLElement {
    connectedCallback() {
      if (this.steps) return;
      this.panels = [...this.querySelectorAll('[data-kit-step]')];
      this.tabs = [...this.querySelectorAll('[data-kit-tab]')];
      this.steps = this.panels.map((panel, index) => new KitStep(panel, index));
      this.sticky = this.querySelector('[data-kit-sticky]');
      this.stickyCta = this.querySelector('[data-kit-sticky-cta]');
      this.kitPrice = Number(this.dataset.kitPrice) || 0;
      this.kitCompare = Number(this.dataset.kitCompare) || 0;
      this.moneyFormat = this.querySelector('[data-money-format]')?.dataset.moneyFormat;
      this.activeIndex = 0;
      this.matchOverrides = new Set();
      this.applyingMatch = false;
      this.submitting = false;

      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onCtaIntersect = this.onCtaIntersect.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      this.tabs.forEach((tab) => tab.addEventListener('keydown', this.onTabKeydown));

      this.setupMatch();
      this.observeCtas();
      this.refresh();
      if (this.sticky) document.body.classList.add('has-kit-sticky');
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('click', this.onClick);
      this.tabs.forEach((tab) => tab.removeEventListener('keydown', this.onTabKeydown));
      this.ctaObserver?.disconnect();
    }

    /* ------------------------------------------------------------ steps */

    activate(index, { focus = false, scroll = false } = {}) {
      if (index < 0 || index >= this.panels.length) return;
      this.activeIndex = index;
      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.refresh();
      this.observeActiveCta();
      if (scroll) scrollToElement(this);
      if (focus) this.panels[index].focus({ preventScroll: true });
    }

    refresh() {
      this.steps.forEach((step, index) => {
        const complete = step.isComplete();
        this.tabs[index]?.classList.toggle('is-complete', complete);
        step.cta.setAttribute('aria-disabled', String(!complete));
      });
      const lastStep = this.steps[this.steps.length - 1];
      lastStep?.cta.setAttribute('aria-disabled', String(!this.steps.every((step) => step.isComplete())));
      this.updateTotals();
      this.updateSticky();
    }

    updateTotals() {
      const addonTotal = this.steps.reduce(
        (sum, step) => sum + step.addons().reduce((stepSum, addon) => stepSum + addon.price, 0),
        0
      );
      const total = this.querySelector('[data-kit-total]');
      const compare = this.querySelector('[data-kit-total-compare]');
      if (total) total.textContent = formatMoney(this.kitPrice + addonTotal, this.moneyFormat);
      if (compare) compare.textContent = formatMoney(this.kitCompare + addonTotal, this.moneyFormat);
    }

    updateSticky() {
      if (!this.sticky) return;
      const step = this.steps[this.activeIndex];
      const counter = (this.dataset.stepCounter || '')
        .replace('[n]', this.activeIndex + 1)
        .replace('[total]', this.steps.length);
      this.sticky.querySelector('[data-kit-sticky-count]').textContent = counter ? `${counter} · ` : '';
      this.sticky.querySelector('[data-kit-sticky-title]').textContent = this.panels[this.activeIndex].dataset.kitStepTitle;
      this.stickyCta.innerHTML = step.cta.innerHTML;
      this.stickyCta.setAttribute('aria-disabled', step.cta.getAttribute('aria-disabled'));
    }

    observeCtas() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;
      this.ctaObserver = new IntersectionObserver(this.onCtaIntersect, { threshold: [0, CTA_VISIBLE_RATIO] });
      this.observeActiveCta();
    }

    observeActiveCta() {
      if (!this.ctaObserver) return;
      this.ctaObserver.disconnect();
      this.ctaObserver.observe(this.steps[this.activeIndex].cta);
    }

    onCtaIntersect(entries) {
      const entry = entries[entries.length - 1];
      const visible = entry.intersectionRatio < CTA_VISIBLE_RATIO;
      this.sticky.classList.toggle('kit-sticky--visible', visible);
      this.sticky.inert = !visible;
    }

    /* ----------------------------------------------------- interaction */

    stepFor(element) {
      const panel = element.closest('[data-kit-step]');
      return panel ? this.steps[this.panels.indexOf(panel)] : null;
    }

    onChange(event) {
      const step = this.stepFor(event.target);
      if (!step) return;
      step.onChange(event.target);
      this.onMatchChange(step, event.target);
      this.refresh();
      step.refreshErrors();
    }

    onInput(event) {
      const field = event.target.closest('[data-kit-field]');
      if (!field || event.target.type !== 'text') return;
      field.classList.toggle('is-filled', event.target.value !== '');
      field.classList.remove('custom-options__field--error');
      const step = this.stepFor(event.target);
      this.refresh();
      step?.refreshErrors();
    }

    onClick(event) {
      const tab = event.target.closest('[data-kit-tab]');
      if (tab) {
        this.activate(this.tabs.indexOf(tab));
        return;
      }
      const cta = event.target.closest('[data-kit-cta], [data-kit-sticky-cta]');
      if (cta) this.onCta();
    }

    onTabKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      const keys = {
        ArrowRight: index + 1,
        ArrowLeft: index - 1,
        Home: 0,
        End: this.tabs.length - 1,
      };
      if (!(event.key in keys)) return;
      event.preventDefault();
      const next = (keys[event.key] + this.tabs.length) % this.tabs.length;
      this.activate(next);
      this.tabs[next].focus();
    }

    onCta() {
      if (this.submitting) return;
      const step = this.steps[this.activeIndex];
      const isLast = this.activeIndex === this.steps.length - 1;

      if (!isLast) {
        const missing = step.showErrors();
        if (missing.length) {
          step.focusField(missing[0]);
          return;
        }
        step.clearErrors();
        this.activate(this.activeIndex + 1, { focus: true, scroll: true });
        return;
      }

      const incompleteIndex = this.steps.findIndex((candidate) => !candidate.isComplete());
      if (incompleteIndex >= 0) {
        const incomplete = this.steps[incompleteIndex];
        if (incompleteIndex !== this.activeIndex) this.activate(incompleteIndex);
        const missing = incomplete.showErrors();
        incomplete.focusField(missing[0]);
        return;
      }
      this.submit();
    }

    /* --------------------------------------------------- match my set */

    setupMatch() {
      this.matchRow = this.querySelector('[data-kit-match]');
      if (!this.matchRow) return;
      this.matchToggle = this.matchRow.querySelector('[data-kit-match-input]');
      this.matchColors = new Set(
        this.matchRow.dataset.kitMatchColors.split('|').map(normalizeColor).filter(Boolean)
      );
      this.matchToggle.addEventListener('change', () => this.onMatchToggle());
      if (this.matchToggle.checked) this.onMatchToggle();
    }

    colorSelect(step) {
      return step.colorField?.querySelector('[data-kit-select]') ?? null;
    }

    onMatchToggle() {
      const leadSelect = this.colorSelect(this.steps[0]);
      this.matchOverrides.clear();
      if (this.matchToggle.checked && leadSelect?.value) this.applyMatch(leadSelect.value);
    }

    onMatchChange(step, target) {
      if (!this.matchToggle?.checked || this.applyingMatch) return;
      if (target !== this.colorSelect(step)) return;
      if (step.index === 0) {
        if (target.value) this.applyMatch(target.value);
        return;
      }
      this.matchOverrides.add(step.index);
    }

    // The collar list is never filtered: a color only carries over when it's a match color
    // and every other product offers it in stock; otherwise the other steps keep their color.
    applyMatch(color) {
      const wanted = normalizeColor(color);
      if (!this.matchColors.has(wanted)) return;
      const targets = this.steps.slice(1)
        .map((step) => {
          const select = this.colorSelect(step);
          const option = select && [...select.options].find(
            (candidate) => candidate.value !== '' && !candidate.disabled && normalizeColor(candidate.value) === wanted
          );
          return { step, select, option };
        })
        .filter(({ select }) => select);
      if (targets.some(({ option }) => !option)) return;

      this.applyingMatch = true;
      targets.forEach(({ step, select, option }) => {
        if (this.matchOverrides.has(step.index)) return;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.applyingMatch = false;
    }

    /* ---------------------------------------------------------- submit */

    setBusy(busy) {
      this.submitting = busy;
      [this.steps[this.steps.length - 1].cta, this.stickyCta].forEach((button) => {
        if (!button) return;
        button.toggleAttribute('aria-busy', busy);
        button.classList.toggle('is-loading', busy);
      });
    }

    showSubmitError(message) {
      const step = this.steps[this.activeIndex];
      step.error.textContent = message;
    }

    submit() {
      if (this.dataset.kitReady !== 'true') {
        this.showSubmitError(this.dataset.unavailableMessage);
        return;
      }

      const payloads = this.steps.map((step) => step.payload());
      const addons = payloads.flatMap((payload) => payload.addons);
      const addonKey = `${this.dataset.kitVariantId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const properties = {};

      payloads.forEach((payload, index) => {
        properties[`_kit_${index + 1}`] = JSON.stringify({
          v: payload.variantId,
          p: payload.properties,
          a: payload.addons.length > 0,
        });
        properties[`_kit_${index + 1}_display`] = payload.display;
      });
      if (addons.length) properties._addon_key = addonKey;

      const items = [
        { id: Number(this.dataset.kitVariantId), quantity: 1, properties },
        ...addons.map((addon) => ({ id: addon.id, quantity: 1, properties: { _addon_for: addonKey } })),
      ];

      this.setBusy(true);
      window.jQuery.ajax({
        url: `${window.Shopify?.routes?.root ?? '/'}cart/add.js`,
        type: 'post',
        dataType: 'json',
        contentType: 'application/json',
        cache: false,
        data: JSON.stringify({ items }),
        success: () => {
          this.setBusy(false);
          this.steps[this.steps.length - 1].clearErrors();
          window.Shopify?.theme?.jsAjaxCart?.showDrawer();
          window.Shopify?.theme?.jsAjaxCart?.updateView();
        },
        error: (request) => {
          this.setBusy(false);
          let message = this.dataset.unavailableMessage;
          try {
            message = JSON.parse(request.responseText).description || message;
          } catch (error) {
            message = this.dataset.unavailableMessage;
          }
          this.showSubmitError(message);
        },
      });
    }
  }

  class KitGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-kit-gallery-track]');
      this.strip = this.querySelector('[data-kit-gallery-thumbs]');
      this.thumbs = [...this.querySelectorAll('[data-kit-gallery-thumb]')];
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onSlideIntersect = this.onSlideIntersect.bind(this);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));

      // Swiping the main image (mobile) moves the current thumbnail with it.
      if (this.thumbs.length && 'IntersectionObserver' in window) {
        this.slideObserver = new IntersectionObserver(this.onSlideIntersect, { root: this.track, threshold: 0.6 });
        [...this.track.children].forEach((slide) => this.slideObserver.observe(slide));
      }
    }

    disconnectedCallback() {
      this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
      this.slideObserver?.disconnect();
    }

    onThumbClick(event) {
      const index = this.thumbs.indexOf(event.currentTarget);
      const slide = this.track.children[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
      this.setCurrent(index);
    }

    onSlideIntersect(entries) {
      const visible = entries.find((entry) => entry.isIntersecting);
      if (!visible) return;
      this.setCurrent([...this.track.children].indexOf(visible.target));
    }

    setCurrent(index) {
      this.thumbs.forEach((thumb, thumbIndex) => {
        if (thumbIndex === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const thumb = this.thumbs[index];
      if (!thumb || !this.strip || this.strip.scrollWidth <= this.strip.clientWidth) return;
      const start = this.strip.scrollLeft;
      const end = start + this.strip.clientWidth;
      if (thumb.offsetLeft >= start && thumb.offsetLeft + thumb.offsetWidth <= end) return;
      this.strip.scrollTo({ left: thumb.offsetLeft - (this.strip.clientWidth - thumb.offsetWidth) / 2 });
    }
  }

  class KitAccordions extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      const desktop = window.matchMedia(DESKTOP_QUERY).matches;
      this.querySelectorAll('[data-kit-accordion]').forEach((accordion) => {
        const open = accordion.dataset[desktop ? 'openDesktop' : 'openMobile'] === 'true';
        this.setOpen(accordion, open);
      });
      this.classList.add('is-ready');
      this.addEventListener('click', this.onClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    setOpen(accordion, open) {
      const toggle = accordion.querySelector('[data-kit-accordion-toggle]');
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      accordion.classList.toggle('is-open', open);
    }

    onClick(event) {
      const toggle = event.target.closest('[data-kit-accordion-toggle]');
      if (!toggle) return;
      const accordion = toggle.closest('[data-kit-accordion]');
      this.setOpen(accordion, toggle.getAttribute('aria-expanded') !== 'true');
    }

    onBlockSelect(event) {
      const accordion = event.target.closest?.('[data-kit-accordion]');
      if (accordion && this.contains(accordion)) this.setOpen(accordion, true);
    }
  }

  customElements.define('kit-builder', KitBuilder);
  customElements.define('kit-gallery', KitGallery);
  customElements.define('kit-accordions', KitAccordions);
})();
